/** Send Email popup — POST /api/email_broadcast multipart (Angular EmailBroadcastComponent). */
import { useEffect, useMemo, useRef, useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { sendBroadcastEmail } from '../../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { sendAnalytics } from '@/lib/utils/analytics'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import modalStyles from '../../styles/modals.module.css'
import type { EmailBroadcastModalProps } from '../../types/modals'


const MAILGUN_HIDDEN_HOST = 'cornerstone.libertyrent.com'
const UNDERWRITER_DEFAULT_FROM = 'apps@libertyrent.com'
const APPLICANT_REPLY_TO_DEFAULT = 'apps@libertyrent.com'
const MAX_ATTACHMENT_MB = 10

/** Strip CKEditor's empty `<p></p>` / `<p>&nbsp;</p>` markup before required-check. */
function isEmptyHtml(html: string): boolean {
  if (!html?.trim()) return true
  return (
    html
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim().length === 0
  )
}

export function EmailBroadcastModal({
  applicantId,
  applicationAllData,
  applicationData,
  onClose,
  onSuccess,
  initialSubject = '',
}: EmailBroadcastModalProps) {
  const tid = 'application-modal-email-broadcast';

  const currentUser = useAppSelector(selectUser)
  const isUnderwriter = currentUser?.current_role?.role_name === 'ROLE_UNDERWRITER'
  const applicantEmail = applicationData?.user_data?.email ?? ''
  const applicationId =
    applicationAllData?.application_id ?? applicationData?.fk_application_id ?? ''

  const [toEmails, setToEmails] = useState(applicantEmail)
  const [subject, setSubject] = useState(initialSubject)
  const [body, setBody] = useState('')
  const [replyTo, setReplyTo] = useState(
    currentUser?.email ?? APPLICANT_REPLY_TO_DEFAULT,
  )
  const [fromEmail, setFromEmail] = useState(
    isUnderwriter ? UNDERWRITER_DEFAULT_FROM : '',
  )
  const [separated, setSeparated] = useState<'0' | '1'>('0')
  const [passthruMailgun, setPassthruMailgun] = useState<'0' | '1'>('0')
  const [sendSms, setSendSms] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [subjectTouched, setSubjectTouched] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    setToEmails(applicantEmail)
    // Angular: for applicant_email, reply_to defaults to apps@libertyrent.com.
    setReplyTo(APPLICANT_REPLY_TO_DEFAULT)
  }, [applicantEmail])

  useEffect(() => {
    setSubject(initialSubject)
    setSubjectTouched(false)
    setErrorMessage('')
  }, [initialSubject])

  const getSubjectValidationError = (value: string) =>
    value.trim().length > 0 ? '' : 'Subject is required'

  const handleSubjectBlur = () => {
    setSubjectTouched(true)
    setErrorMessage(getSubjectValidationError(subject))
  }

  useEffect(() => {
    if (subjectTouched) {
      setErrorMessage(getSubjectValidationError(subject))
    }
  }, [subject, subjectTouched])

  const showMailgun = useMemo(() => {
    if (typeof window === 'undefined') return true
    return window.location.hostname !== MAILGUN_HIDDEN_HOST
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    const oversize = files.find((f) => f.size / 1024 / 1024 > MAX_ATTACHMENT_MB)
    if (oversize) {
      showError(`File Size Exceeds Allowable Limit of ${MAX_ATTACHMENT_MB} MB.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setAttachments([])
      return
    }
    setAttachments(files)
  }

  const canSubmit =
    toEmails.trim().length > 0 &&
    subject.trim().length > 0 &&
    !isEmptyHtml(body) &&
    !submitting

  const handleSubmit = async () => {
    const subjectValidationError = getSubjectValidationError(subject)
    if (subjectValidationError) {
      setSubjectTouched(true)
      setErrorMessage(subjectValidationError)
      return
    }
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('to_emails', encodeURIComponent(toEmails.trim()))
      fd.append('email_subject', subject.trim())
      fd.append('email_body', body)
      fd.append('seperated_to_address', separated)
      fd.append('passthru_mailgun', passthruMailgun)
      fd.append('send_sms', sendSms ? '1' : '')
      fd.append('frompage', 'applicant_email')
      fd.append('blast_type', 'applicant_email')
      if (replyTo.trim()) fd.append('reply_to', encodeURIComponent(replyTo.trim()))
      if (isUnderwriter && fromEmail.trim()) {
        fd.append('from_email', encodeURIComponent(fromEmail.trim()))
      }
      if (applicationId) fd.append('application_id', String(applicationId))
      if (applicantId) fd.append('applicant_id', String(applicantId))
      attachments.forEach((file, i) => {
        fd.append(`attachment[${i}]`, file)
      })
      await sendBroadcastEmail(fd)
      sendAnalytics('click', 'send broadcast email', '')
      showSuccess('Mail Sent Successfully!')
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : 'Failed to send email.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]'
  const labelCls = 'block text-sm font-medium text-slate-700 mb-1'
  const radioWrapCls = 'flex items-center gap-6'
  const radioLabelCls = 'flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer'

  const attachmentLabel =
    attachments.length === 0
      ? 'Choose file...'
      : attachments.length === 1
        ? attachments[0].name
        : `${attachments.length} files selected`

  return (
    <OnScreenModal
      isOpen={true}
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      title="Send Email"
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      footer={
        <div
          className={modalStyles.modalFooterActionGroup}
          data-testid="application-modal-email-broadcast-footer"
        >
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={modalStyles.modalActionPrimary}
            data-testid="application-modal-email-broadcast-send-button"
          >
            {submitting ? 'Sending...' : 'Send'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={modalStyles.modalActionSecondary}
            data-testid="application-modal-email-broadcast-cancel-button"
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="space-y-4" data-testid={`${tid}-body`}>
        {errorMessage ? (
          <div
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
            data-testid="application-modal-email-broadcast-error-message"
          >
            <p>{errorMessage}</p>
          </div>
        ) : null}

        <div>
          <label className={labelCls} data-testid="application-modal-email-broadcast-to-label">
            TO<span className="text-red-500">*</span>{' '}
            <span className="font-normal text-xs text-slate-500">
              (For example: email1@gmail.com,email2@gmail.com)
            </span>
          </label>
          <input
            type="text"
            className={inputCls}
            value={toEmails}
            onChange={(e) => setToEmails(e.target.value)}
            placeholder="TO"
            data-testid="application-modal-email-broadcast-to-input"
          />
        </div>

        <div>
          <label className={labelCls} data-testid="application-modal-email-broadcast-send-separately-label">
            Send Separately<span className="text-red-500">*</span>
          </label>
          <div className={radioWrapCls}>
            <label className={radioLabelCls} data-testid="application-modal-email-broadcast-send-separately-yes-label">
              <input
                type="radio"
                name="separated_to_address"
                checked={separated === '1'}
                onChange={() => setSeparated('1')}
                data-testid="application-modal-email-broadcast-send-separately-yes-radio"
              />
              Yes
            </label>
            <label className={radioLabelCls} data-testid="application-modal-email-broadcast-send-separately-no-label">
              <input
                type="radio"
                name="separated_to_address"
                checked={separated === '0'}
                onChange={() => setSeparated('0')}
                data-testid="application-modal-email-broadcast-send-separately-no-radio"
              />
              No
            </label>
          </div>
        </div>

        {isUnderwriter && (
          <div>
            <label className={labelCls} data-testid="application-modal-email-broadcast-from-email-label">From Email</label>
            <input
              type="text"
              className={inputCls}
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="From Email"
              data-testid="application-modal-email-broadcast-from-email-input"
            />
          </div>
        )}

        <div>
          <label className={labelCls} data-testid="application-modal-email-broadcast-reply-to-label">Reply To</label>
          <input
            type="email"
            className={inputCls}
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="Reply To"
            data-testid="application-modal-email-broadcast-reply-to-input"
          />
        </div>

        <div>
          <label className={labelCls} data-testid="application-modal-email-broadcast-subject-label">
            Email Subject<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`${inputCls}${subjectTouched && !subject.trim() ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onBlur={handleSubjectBlur}
            placeholder="Email Subject*"
            data-testid="application-modal-email-broadcast-subject-input"
          />
          {subjectTouched && !subject.trim() ? (
            <p
              className="mt-1 text-xs text-red-600"
              role="alert"
              data-testid="application-modal-email-broadcast-subject-error"
            >
              Subject is required
            </p>
          ) : null}
        </div>

        <div>
          <label className={labelCls} data-testid="application-modal-email-broadcast-body-label">
            Email Body<span className="text-red-500">*</span>
          </label>
          <CKEditor4 value={body} onChange={setBody} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} data-testid="application-modal-email-broadcast-attachments-label">Attachments</label>
            <div className="flex items-stretch overflow-hidden rounded-md border border-slate-300 bg-white">
              <button
                type="button"
                className="flex-1 truncate px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50"
                onClick={() => fileInputRef.current?.click()}
                data-testid="application-modal-email-broadcast-attachments-display-button"
              >
                {attachmentLabel}
              </button>
              <button
                type="button"
                className="shrink-0 border-l border-slate-300 bg-[rgb(21_151_255)]/10 px-4 py-2 text-sm font-semibold text-[rgb(21_151_255)] hover:bg-[rgb(21_151_255)]/15"
                onClick={() => fileInputRef.current?.click()}
                data-testid="application-modal-email-broadcast-attachments-upload-button"
              >
                Upload
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="sr-only"
                onChange={handleFileChange}
                data-testid="application-modal-email-broadcast-attachments-input"
              />
            </div>
            <p className="mt-1 text-xs text-slate-500">Max {MAX_ATTACHMENT_MB} MB per file.</p>
          </div>

          <div className="space-y-3">
            <label className={radioLabelCls} data-testid="application-modal-email-broadcast-send-sms-label">
              <input
                type="checkbox"
                checked={sendSms}
                onChange={(e) => setSendSms(e.target.checked)}
                data-testid="application-modal-email-broadcast-send-sms-checkbox"
              />
              Send Notification SMS
            </label>
            {showMailgun && (
              <div>
                <label className={labelCls} data-testid="application-modal-email-broadcast-passthru-mailgun-label">Passthru Mailgun</label>
                <div className={radioWrapCls}>
                  <label className={radioLabelCls} data-testid="application-modal-email-broadcast-passthru-mailgun-yes-label">
                    <input
                      type="radio"
                      name="passthru_mailgun"
                      checked={passthruMailgun === '1'}
                      onChange={() => setPassthruMailgun('1')}
                      data-testid="application-modal-email-broadcast-passthru-mailgun-yes-radio"
                    />
                    Yes
                  </label>
                  <label className={radioLabelCls} data-testid="application-modal-email-broadcast-passthru-mailgun-no-label">
                    <input
                      type="radio"
                      name="passthru_mailgun"
                      checked={passthruMailgun === '0'}
                      onChange={() => setPassthruMailgun('0')}
                      data-testid="application-modal-email-broadcast-passthru-mailgun-no-radio"
                    />
                    No
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </OnScreenModal>
  )
}
